import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CdkDragDrop, transferArrayItem } from '@angular/cdk/drag-drop';
import { NbDialogService } from '@nebular/theme';
import { SubscribedComponent } from '../../subscribed.component';
import { SubmissionService } from '../../shared/submission.service';
import { NotifierService } from '../../shared/notifier.service';
import { Submission, SubmissionStatus, SUBMISSION_PIPELINE } from '../../interfaces/submission';
import {
  LossReasonDialogComponent,
  LossDialogResult,
  LossDialogConfirmed,
  LOSS_DIALOG_CANCEL,
} from './loss-reason-dialog.component';
import {
  InterviewDateDialogComponent,
  InterviewDialogResult,
  INTERVIEW_DIALOG_CANCEL,
} from './interview-date-dialog.component';
import { ConvertProjectDialogComponent } from './convert-project-dialog/convert-project-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/** A submission enriched with its parent opportunity context for the card. */
interface BoardCard extends Submission {
  opportunityTitle: string;
  endClientName: string | null;
}

/**
 * Maps each pipeline status to a restrained stage colour used for the card's
 * left accent border and the column-header tint:
 *   draft/preparing -> grey, sent/ready_to_send -> blue,
 *   interview_* -> indigo, won -> royal (not green), lost -> red,
 *   withdrawn -> muted grey.
 */
const STAGE_ACCENT: Record<SubmissionStatus, string> = {
  draft: 'grey',
  preparing: 'grey',
  ready_to_send: 'blue',
  sent: 'blue',
  interview_scheduled: 'indigo',
  interview_done: 'indigo',
  won: 'royal',
  lost: 'red',
  withdrawn: 'muted',
};

/** All visible pipeline stages including the trailing "Withdrawn" column. */
const BOARD_PIPELINE: SubmissionStatus[] = [...SUBMISSION_PIPELINE, 'withdrawn'];

interface BoardColumn {
  status: SubmissionStatus;
  cards: BoardCard[];
  isWithdrawn?: boolean;
}

@Component({
  selector: 'bst-submissions',
  templateUrl: './submissions.component.html',
  styleUrls: ['./submissions.component.scss'],
  standalone: false
})
export class SubmissionsComponent extends SubscribedComponent implements OnInit {

  @ViewChild('boardEl') boardEl?: ElementRef<HTMLElement>;

  columns: BoardColumn[] = [];
  errorMessage: string | null = null;
  searchQuery = '';
  private searchDebounce = new Subject<string>();

  /** Connected drop-list ids so cards can be dragged between any column. */
  get columnIds(): string[] {
    return this.columns.map(c => this.dropListId(c.status));
  }

  /** True when every column is empty — drives the single board-wide empty state. */
  get isBoardEmpty(): boolean {
    return this.columns.every(c => !c.cards.length);
  }

  /** Restrained stage colour suffix for a status (see STAGE_ACCENT). */
  stageAccent(status: SubmissionStatus): string {
    return STAGE_ACCENT[status] ?? 'grey';
  }

  /** Full pipeline used by the per-card stage select (drag-free alternative). */
  readonly pipeline = SUBMISSION_PIPELINE;

  /** True when the card has a CV ready (draft or finalized, or GeneratedCv_id set). */
  hasCvReady(card: BoardCard): boolean {
    if (card.GeneratedCv_id != null) { return true; }
    const status = card.application_package?.cv_status;
    return status === 'draft' || status === 'finalized';
  }

  constructor(private submissionService: SubmissionService,
              private dialogService: NbDialogService,
              private notifier: NotifierService,
              private router: Router,
              private translate: TranslateService) {
    super();
  }

  /** Open a card's submission detail (the parent opportunity is one back-link away). */
  openSubmission(card: BoardCard): void {
    void this.router.navigate(['submissions', card.id], { queryParams: { from: 'board' } });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.subs.push(this.searchDebounce.pipe(debounceTime(300)).subscribe(() => void this.load()));
    void this.load();
  }

  dropListId(status: SubmissionStatus): string {
    return `col-${status}`;
  }

  /**
   * Build the board from the tenant-wide GET /submissions/board endpoint;
   * each row carries its opportunity + end-client context for the card.
   * Withdrawn cards get their own trailing column.
   */
  private async load(): Promise<void> {
    this.loading.next(true);
    this.errorMessage = null;
    this.columns = BOARD_PIPELINE.map(status => ({
      status,
      cards: [],
      isWithdrawn: status === 'withdrawn',
    }));
    try {
      const cards: BoardCard[] = [];
      let page = 1;
      // Safety cap: 10 pages × 200 = 2000 cards. Beyond that, filters are the tool.
      for (; page <= 10; page++) {
        const res = await this.submissionService.board({
          page,
          per_page: 200,
          search: this.searchQuery.trim(),
        });
        // HTTP failures reject into the catch below; an empty body on a 2xx is
        // still a failure, never an empty board.
        if (!res) {
          this.errorMessage = this.translate.instant('crm.board.load_error');
          return;
        }
        const rows: Submission[] = res?.data ?? [];
        for (const s of rows) {
          cards.push({
            ...s,
            opportunityTitle: s.opportunity?.title ?? '',
            endClientName: s.opportunity?.end_client?.name ?? null,
          });
        }
        if (res.current_page >= res.last_page) { break; }
      }

      for (const card of cards) {
        const column = this.columns.find(c => c.status === card.status);
        // All statuses are now visible (withdrawn has its own column).
        if (column) { column.cards.push(card); }
      }
    } catch (e) {
      console.error('submissions board load failed', e);
      this.errorMessage = this.translate.instant('crm.board.load_error');
    } finally {
      this.loading.next(false);
    }
  }

  /** Debounced free-text search across consultant, opportunity title and end client. */
  onSearchChange(value: string): void {
    this.searchDebounce.next(value);
  }

  clearSearch(): void {
    this.searchQuery = '';
    void this.load();
  }

  consultantName(card: BoardCard): string {
    if (!card.consultant) { return `#${card.consultant_User_id}`; }
    return `${card.consultant.name_first} ${card.consultant.name_last}`.trim();
  }

  /**
   * CDK drop handler. Moves the card optimistically into the target column,
   * then persists. On a drop into `lost` we open the loss-reason dialog. If
   * the server rejects, we reload to resync the board with the truth.
   */
  async onDrop(event: CdkDragDrop<BoardCard[]>, targetStatus: SubmissionStatus): Promise<void> {
    if (event.previousContainer === event.container) {
      // Reorder within the same column is cosmetic; no status change to persist.
      return;
    }

    const card = event.previousContainer.data[event.previousIndex];

    let lossReason: string | undefined;
    let lossNote: string | undefined;
    let interviewAt: string | undefined;
    if (targetStatus === 'lost') {
      const result = await this.askLossReason();
      // Cancel aborts the move.
      if (result === LOSS_DIALOG_CANCEL) { return; }
      const confirmed = result as LossDialogConfirmed;
      lossReason = confirmed.reason ?? undefined;
      lossNote = confirmed.note ?? undefined;
    }
    if (targetStatus === 'interview_scheduled') {
      const picked = await this.askInterviewDate();
      if (picked === INTERVIEW_DIALOG_CANCEL) { return; }
      interviewAt = picked ?? undefined;
    }

    // Optimistic move + local status update.
    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );
    card.status = targetStatus;

    await this.persistStatus(card, targetStatus, lossReason, lossNote, interviewAt);
    this.scrollToColumn(targetStatus);
  }

  /**
   * Drag-free, keyboard-operable alternative to dragging: the per-card stage
   * <nb-select> calls this on change. Moves the card to the chosen column
   * locally, then runs the same persist path as a drop (incl. the loss-reason
   * dialog when moving to `lost`). Bound via [ngModel] + (selectedChange) so it
   * doesn't re-fire on init.
   */
  async onStageChange(card: BoardCard, targetStatus: SubmissionStatus): Promise<void> {
    if (card.status === targetStatus) { return; }

    let lossReason: string | undefined;
    let lossNote: string | undefined;
    let interviewAt: string | undefined;
    if (targetStatus === 'lost') {
      const result = await this.askLossReason();
      if (result === LOSS_DIALOG_CANCEL) {
        // Revert the select to the truth — reload is the simplest resync.
        await this.load();
        return;
      }
      const confirmed = result as LossDialogConfirmed;
      lossReason = confirmed.reason ?? undefined;
      lossNote = confirmed.note ?? undefined;
    }
    if (targetStatus === 'interview_scheduled') {
      const picked = await this.askInterviewDate();
      if (picked === INTERVIEW_DIALOG_CANCEL) {
        await this.load();
        return;
      }
      interviewAt = picked ?? undefined;
    }

    this.moveCardLocally(card, targetStatus);
    await this.persistStatus(card, targetStatus, lossReason, lossNote, interviewAt);
    this.scrollToColumn(targetStatus);
  }

  /** Open the loss-reason dialog and resolve to its result (or CANCEL). */
  private async askLossReason(): Promise<LossDialogResult> {
    const result = await this.dialogService
      .open<LossReasonDialogComponent>(LossReasonDialogComponent)
      .onClose.toPromise();
    // Esc/backdrop dismissal resolves undefined; normalise to an explicit
    // cancel so callers' `=== LOSS_DIALOG_CANCEL` check aborts the move instead
    // of dereferencing `.reason` on undefined and throwing.
    return (result ?? LOSS_DIALOG_CANCEL) as LossDialogResult;
  }

  /** Open the interview-date dialog (Gespreksdatum) and resolve to its result. */
  private async askInterviewDate(): Promise<InterviewDialogResult> {
    const result = await this.dialogService
      .open<InterviewDateDialogComponent>(InterviewDateDialogComponent)
      .onClose.toPromise();
    // Esc/backdrop dismissal resolves undefined; treat as cancel (abort move).
    return (result === undefined ? INTERVIEW_DIALOG_CANCEL : result) as InterviewDialogResult;
  }

  /** Move a card between columns in the local model (used by the stage select). */
  private moveCardLocally(card: BoardCard, targetStatus: SubmissionStatus): void {
    const from = this.columns.find(c => c.cards.includes(card));
    const to = this.columns.find(c => c.status === targetStatus);
    if (from) {
      const idx = from.cards.indexOf(card);
      if (idx > -1) { from.cards.splice(idx, 1); }
    }
    card.status = targetStatus;
    if (to) { to.cards.push(card); }
  }

  /**
   * Persist a status change and surface the outcome via toast. On failure we
   * also reload so the board reflects persisted truth, not the optimistic move.
   *
   * When the card moves to `won` and has not yet been converted, we reload the
   * board first (so sibling submissions appear as Withdrawn — the backend
   * supersedes them server-side), then open the convert-to-project dialog.
   */
  private async persistStatus(card: BoardCard, targetStatus: SubmissionStatus, lossReason?: string, lossNote?: string, interviewAt?: string): Promise<void> {
    try {
      await this.submissionService.setStatus(card.id, targetStatus, lossReason, lossNote, interviewAt);
      // Skip the generic "Moved to Placed" toast for won — the convert dialog flow
      // provides its own "Project created" feedback (or the user chose to skip it).
      if (targetStatus !== 'won') {
        void this.notifier.notify(this.translate.instant('crm.board.moved_to') + ' ' + this.translate.instant('crm.sub.' + targetStatus), null, 'success');
      }

      if (targetStatus === 'won' && !card.converted_Project_id) {
        // Reload so sibling withdrawals (done server-side) are reflected.
        await this.load();
        await this.promptConvertToProject(card);
      }
    } catch (e) {
      console.error('submission setStatus failed', e);
      void this.notifier.notify(this.translate.instant('crm.board.update_error'), null, 'danger');
      // Resync from server so the board doesn't lie about persisted state.
      await this.load();
    }
  }

  /**
   * Open the "Create project?" dialog after a successful placement. If the
   * recruiter confirms and the API call succeeds, toast the result. If they
   * skip, do nothing — the submission-detail page's "Convert to project" button
   * remains the later path.
   */
  private async promptConvertToProject(card: BoardCard): Promise<void> {
    const project = await this.dialogService
      .open<ConvertProjectDialogComponent>(ConvertProjectDialogComponent, {
        context: {
          consultantName: this.consultantName(card),
          opportunityTitle: card.opportunityTitle,
          submissionId: card.id,
        },
      })
      .onClose.toPromise();

    if (project) {
      void this.notifier.notify(this.translate.instant('crm.convert.created_toast'), null, 'success');
      const consultantUserId = card.consultant_User_id;
      if (project.id && consultantUserId) {
        void this.router.navigate(['/projects', project.id], {
          queryParams: { edit: 1, assign: consultantUserId }
        });
      }
    }
  }

  /** True when a card can be removed (not won and not yet converted). */
  canRemove(card: BoardCard): boolean {
    return card?.status !== 'won' && !this.isFrozen(card);
  }

  isFrozen(card: BoardCard): boolean {
    return !!card?.converted_Project_id;
  }

  /** Confirm then delete the submission; reload the board so the card disappears. */
  async removeSubmission(card: BoardCard): Promise<void> {
    if (!confirm(this.translate.instant('crm.remove_confirm'))) { return; }
    try {
      await this.submissionService.deleteSubmission(card.id);
      await this.load();
    } catch (e) {
      console.error('submission delete failed', e);
    }
  }

  /**
   * Scroll the board horizontally so the destination column is visible.
   * Uses setTimeout(0) to let Angular flush the DOM after the move/reload.
   * Fails silently when the board element or column element is not found.
   */
  private scrollToColumn(status: SubmissionStatus): void {
    setTimeout(() => {
      const board = this.boardEl?.nativeElement;
      if (!board) { return; }
      // Each board-column carries a data attribute matching the drop-list id.
      const colEl = board.querySelector<HTMLElement>(`[id="${this.dropListId(status)}"]`);
      if (colEl) {
        colEl.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      }
    }, 0);
  }

  get headerTitle(): string | { title: string; translate: boolean | Object } {
    return 'CRM Submissions';
  }

}
